import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import { IGpxWaypointType } from './GpxRouteApi';

/**
 * Builds and caches the teardrop map-marker styles for waypoint types. The
 * glyph inside the marker comes from the type's MarkerSvg (raw SVG fragment
 * served by the waypoint-types API), so new types are data-driven — no code
 * change needed to add one.
 */
export class GpxIconFactory {

    private types: Map<number, IGpxWaypointType> = new Map();
    private styleCache: Map<number, Style> = new Map();
    private fallbackTypeId: number | null = null;

    /** Marker canvas: a 32×42 teardrop with the glyph centred in the bulb. */
    private static readonly TEARDROP_PATH =
        'M16 1C7.7 1 1 7.7 1 16c0 10.5 12.6 22.7 14.1 24.1c0.5 0.5 1.3 0.5 1.8 0C18.4 38.7 31 26.5 31 16C31 7.7 24.3 1 16 1z';

    public setTypes(types: IGpxWaypointType[]): void {
        this.types.clear();
        this.styleCache.clear();
        this.fallbackTypeId = null;
        for (const type of types) {
            this.types.set(type.id, type);
            if (this.fallbackTypeId === null) {
                this.fallbackTypeId = type.id;
            }
        }
    }

    public getType(typeId: number): IGpxWaypointType | undefined {
        return this.types.get(typeId) ?? (this.fallbackTypeId !== null ? this.types.get(this.fallbackTypeId) : undefined);
    }

    public getAllTypes(): IGpxWaypointType[] {
        return Array.from(this.types.values());
    }

    public getStyle(typeId: number): Style | null {
        const type = this.getType(typeId);
        if (!type) {
            return null;
        }

        let style = this.styleCache.get(type.id);
        if (!style) {
            style = new Style({
                image: new Icon({
                    src: this.buildMarkerDataUri(type),
                    anchor: [0.5, 1],
                    anchorXUnits: 'fraction' as any,
                    anchorYUnits: 'fraction' as any
                }),
                zIndex: 1000
            });
            this.styleCache.set(type.id, style);
        }

        return style;
    }

    /** A small standalone data URI of the marker, for previews in dialogs/lists. */
    public getMarkerDataUri(typeId: number): string | null {
        const type = this.getType(typeId);
        return type ? this.buildMarkerDataUri(type) : null;
    }

    private buildMarkerDataUri(type: IGpxWaypointType): string {
        const glyph = this.buildNestedGlyph(type.markerSvg);
        const svg =
            `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">` +
            `<path d="${GpxIconFactory.TEARDROP_PATH}" fill="${type.colourHex}" stroke="#ffffff" stroke-width="2"/>` +
            glyph +
            `</svg>`;
        return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    }

    /**
     * Nests the type's raw SVG fragment inside the marker bulb: injects
     * position/size and a white fill onto its root <svg> element. The nested
     * svg scales the glyph via its own viewBox.
     */
    private buildNestedGlyph(markerSvg: string): string {
        if (!markerSvg || markerSvg.indexOf('<svg') < 0) {
            return '';
        }
        return markerSvg.replace('<svg', '<svg x="8" y="8" width="16" height="16" fill="#ffffff"');
    }
}
